/* ===== engine/render/chunkcache.ts — 타일 청크 캐시: 안 움직이는 칸을 청크째 한 장에 구워 두고 한 번에 얹는다 ===== */
/* Godot TileMap 의 사분면(quadrant) 처럼 N×N 칸을 화면 밖 캔버스 하나에 그려 두고, 프레임마다 칸 수천 개 대신 청크 수십 장만 얹는다.
   ★ 청크가 낡았는지는 **칸 서명**으로 잰다 — 청크와 그 둘레 한 칸의 서명(sig)을 적어 두고 그릴 때마다 대조한다. 그래서 세계가 set() 을 거치지 않고
   tiles 를 바꿔도(생성 · 작물 · 무너지는 바닥 …) 놓치지 않고, 이웃을 보고 그리는 칸(이음 타일 · 윗면 빛)도 둘레가 바뀌면 다시 굽는다.
   한 칸보다 멀리 보는 그림(이끼 밀도 따위)은 `refresh` 초마다 한 장씩 돌아가며 다시 굽는 것으로 따라잡는다.
   ★ 굽는 그림은 1 배(칸 = cell px)다 — 1 배 픽셀 그림을 가장 가까운 화소로 늘리는 화면에서는 칸마다 그리는 것과 화소가 같다. */

export interface ChunkCacheOpts {
  /** 칸 한 변(px) */ cell: number;
  /** 청크 한 변(칸) — 16 이면 352px(칸 22) · 한 장 0.5MB */ size?: number;
  /** 칸 (x, y) 의 서명 — 그림이 달라지면 달라져야 한다(세계 밖은 -1 따위) */ sig: (x: number, y: number) => number;
  /** 청크 그리기 — g 는 청크 왼쪽 위가 (0, 0). (x0, y0) ~ (x1, y1) 칸(끝 포함)을 칸 (x - x0) * cell 자리에 그린다 */
  paint: (g: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number) => void;
  /** 한 프레임에 새로 굽는 청크 수 — 넘치면 그 청크는 이번 프레임에 ready 가 아니다(부르는 쪽이 칸마다 그린다) */ budget?: number;
  /** 보이는 청크마다 이 초가 지나면 다시 굽는다(한 칸보다 먼 이웃 · 시간 따라 바뀌는 캐시를 따라잡게) · 0 = 안 함 */ refresh?: number;
}

interface Chunk { cv: HTMLCanvasElement; g: CanvasRenderingContext2D; sig: Int32Array; ok: boolean; t: number; used: number }

export class ChunkCache {
  declare o: Required<ChunkCacheOpts>; declare map: Map<number, Chunk>; declare key: string; declare frame: number; declare built: number;
  declare pool: Chunk[]; declare stamp: number; declare stats: { built: number; hit: number; fallback: number };
  constructor(o: ChunkCacheOpts) {
    this.o = { size: 16, budget: 6, refresh: 2.5, ...o };
    this.map = new Map(); this.key = ''; this.stamp = -2; this.frame = 0; this.built = 0; this.pool = [];
    this.stats = { built: 0, hit: 0, fallback: 0 };
  }
  /** 청크 (cx, cy) 의 열쇠 — 세계 너비와 상관없이 겹치지 않게 */
  private id(cx: number, cy: number) { return (cy + 1024) * 4096 + (cx + 1024); }
  /** 통째로 버린다(아틀라스가 바뀌었을 때 · 세계가 바뀌었을 때) */
  clear(): void { for (const c of this.map.values()) this.pool.push(c); this.map.clear(); }
  /** 보이는 칸 범위의 청크를 준비한다 — key(아틀라스 판 · 잿빛 단계 따위 전역 상태)가 바뀌면 다 버린다 · now 는 초 */
  prepare(tx0: number, ty0: number, tx1: number, ty1: number, key: string, now: number, stamp = -1): void {
    if (key !== this.key) { this.clear(); this.key = key; }
    const N = this.o.size, cx0 = Math.floor(tx0 / N), cy0 = Math.floor(ty0 / N), cx1 = Math.floor(tx1 / N), cy1 = Math.floor(ty1 / N);
    /* stamp(세계의 바뀜 표)가 그대로면 서명 대조는 네 프레임에 한 번씩 돌아가며만 — set() 을 안 거친 바뀜은 그것으로 따라잡는다 */
    const calm = stamp >= 0 && stamp === this.stamp; this.stamp = stamp;
    this.frame++; let budget = this.o.budget, nth = 0, stale: Chunk | null = null, staleId = 0, staleT = Infinity;
    for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) {
      const id = this.id(cx, cy); let ch = this.map.get(id);
      if (!ch) { ch = this.alloc(); this.map.set(id, ch); }
      ch.used = this.frame;
      if (ch.ok && (!calm || ((nth++ + this.frame) & 3) === 0) && !this.same(ch, cx, cy)) ch.ok = false;
      if (!ch.ok) { if (budget > 0) { budget--; this.build(ch, cx, cy, now); } else this.stats.fallback++; }
      else if (this.o.refresh && now - ch.t > this.o.refresh && ch.t < staleT) { stale = ch; staleId = id; staleT = ch.t; }
    }
    if (stale && budget > 0) { const cx = (staleId % 4096) - 1024, cy = Math.floor(staleId / 4096) - 1024; this.build(stale, cx, cy, now); }   // 한 프레임에 한 장만 돌아가며
    /* 안 보인 지 오래된 청크는 놓는다 — 보이는 수의 두 배 남짓만 들고 있는다 */
    const keep = (cx1 - cx0 + 1) * (cy1 - cy0 + 1) * 2 + 8;
    if (this.map.size > keep) {
      const old = [...this.map].filter(([, c]) => c.used !== this.frame).sort((a, b) => a[1].used - b[1].used);
      for (let i = 0; i < old.length && this.map.size > keep; i++) { this.map.delete(old[i][0]); this.pool.push(old[i][1]); }
    }
  }
  /** 칸 (tx, ty) 가 든 청크가 이번 프레임에 구워져 있나 — 아니면 부르는 쪽이 그 칸을 직접 그린다 */
  ready(tx: number, ty: number): boolean {
    const N = this.o.size, ch = this.map.get(this.id(Math.floor(tx / N), Math.floor(ty / N)));
    return !!ch && ch.ok && ch.used === this.frame;
  }
  /** 준비된 청크를 화면에 얹는다 — 칸 범위 안 것만 */
  blit(c: CanvasRenderingContext2D, tx0: number, ty0: number, tx1: number, ty1: number, camX: number, camY: number): void {
    const N = this.o.size, S = N * this.o.cell;
    for (let cy = Math.floor(ty0 / N); cy <= Math.floor(ty1 / N); cy++) for (let cx = Math.floor(tx0 / N); cx <= Math.floor(tx1 / N); cx++) {
      const ch = this.map.get(this.id(cx, cy));
      if (ch && ch.ok && ch.used === this.frame) { c.drawImage(ch.cv, cx * S - camX, cy * S - camY); this.stats.hit++; }
    }
  }
  private alloc(): Chunk {
    const p = this.pool.pop(); if (p) { p.ok = false; return p; }
    const N = this.o.size, S = N * this.o.cell, cv = document.createElement('canvas');
    cv.width = cv.height = S;
    const g = cv.getContext('2d')!; g.imageSmoothingEnabled = false;
    return { cv, g, sig: new Int32Array((N + 2) * (N + 2)), ok: false, t: 0, used: 0 };
  }
  /** 청크와 둘레 한 칸의 서명이 적어 둔 것과 같은가 */
  private same(ch: Chunk, cx: number, cy: number): boolean {
    const N = this.o.size, x0 = cx * N - 1, y0 = cy * N - 1, W = N + 2, s = ch.sig, f = this.o.sig;
    for (let j = 0; j < W; j++) for (let i = 0; i < W; i++) if (s[j * W + i] !== f(x0 + i, y0 + j)) return false;
    return true;
  }
  private build(ch: Chunk, cx: number, cy: number, now: number): void {
    const N = this.o.size, x0 = cx * N, y0 = cy * N, W = N + 2, f = this.o.sig;
    for (let j = 0; j < W; j++) for (let i = 0; i < W; i++) ch.sig[j * W + i] = f(x0 - 1 + i, y0 - 1 + j);
    const g = ch.g;
    g.setTransform(1, 0, 0, 1, 0, 0); g.clearRect(0, 0, ch.cv.width, ch.cv.height);
    g.globalAlpha = 1; g.globalCompositeOperation = 'source-over'; g.imageSmoothingEnabled = false;
    this.o.paint(g, x0, y0, x0 + N - 1, y0 + N - 1);
    ch.ok = true; ch.t = now; this.stats.built++;
  }
}
